import {
  Badge,
  Box,
  Breadcrumb,
  Button,
  Container,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Link, useNavigate } from "@tanstack/react-router"

import {
  type QuestionAuditLogEntry,
  getQuestionAuditLog,
} from "@/api/questionAudit"

interface QuestionAuditTimelineProps {
  questionId: string
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "—"
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return "—"
    }
    return value.map((item) => formatValue(item)).join(", ")
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (_error) {
      return String(value)
    }
  }

  return String(value)
}

const formatTimestamp = (timestamp: string) =>
  new Date(timestamp).toLocaleString()

const getActorLabel = (entry: QuestionAuditLogEntry) => {
  if (!entry.actor) {
    return "System"
  }

  if (entry.actor.full_name) {
    return entry.actor.full_name
  }

  if (entry.actor.email) {
    return entry.actor.email
  }

  return "Unknown user"
}

function QuestionAuditTimeline({ questionId }: QuestionAuditTimelineProps) {
  const navigate = useNavigate()
  const { data, isLoading, isError } = useQuery({
    queryKey: ["questionAuditHistory", questionId],
    queryFn: () => getQuestionAuditLog(questionId),
  })

  if (isLoading) {
    return (
      <Container maxW="container.lg" py={12}>
        <HStack justify="center">
          <Spinner size="lg" />
          <Text>Loading history…</Text>
        </HStack>
      </Container>
    )
  }

  if (isError || !data) {
    return (
      <Container maxW="container.lg" py={12}>
        <Text color="red.500">
          Unable to load question history. Please try again later.
        </Text>
      </Container>
    )
  }

  return (
    <Container maxW="container.lg" py={8}>
      <VStack align="stretch" gap={6}>
        {/* Breadcrumbs */}
        <Breadcrumb.Root>
          <Breadcrumb.List>
            <Breadcrumb.Item>
              <Breadcrumb.Link asChild>
                <Link to="/questions">Questions</Link>
              </Breadcrumb.Link>
            </Breadcrumb.Item>
            <Breadcrumb.Separator />
            <Breadcrumb.Item>
              <Breadcrumb.Link asChild>
                <Link to={`/questions/${questionId}/edit`}>Edit Question</Link>
              </Breadcrumb.Link>
            </Breadcrumb.Item>
            <Breadcrumb.Separator />
            <Breadcrumb.Item>
              <Breadcrumb.CurrentLink>History</Breadcrumb.CurrentLink>
            </Breadcrumb.Item>
          </Breadcrumb.List>
        </Breadcrumb.Root>

        <Heading size="lg">Question History</Heading>

        {data.length === 0 ? (
          <Box borderWidth="1px" borderRadius="lg" p={6}>
            <Text>No changes have been recorded for this question yet.</Text>
          </Box>
        ) : (
          <VStack align="stretch" gap={4}>
            {data.map((entry) => (
              <Box
                key={entry.id}
                borderWidth="1px"
                borderRadius="lg"
                p={6}
                bg="bg.surface"
              >
                <HStack justify="space-between" align="start">
                  <VStack align="start" gap={1}>
                    <HStack gap={3}>
                      <Badge
                        variant="outline"
                        colorPalette="blue"
                        data-testid={`audit-action-${entry.action.toLowerCase()}`}
                      >
                        {entry.action.toUpperCase()}
                      </Badge>
                      <Text fontWeight="semibold">{getActorLabel(entry)}</Text>
                    </HStack>
                    <Text color="fg.muted">
                      {formatTimestamp(entry.created_at)}
                    </Text>
                  </VStack>
                </HStack>

                <Box borderTopWidth="1px" borderColor="border" my={4} />

                <VStack
                  as="ul"
                  align="stretch"
                  gap={3}
                  listStyleType="none"
                  margin={0}
                  padding={0}
                >
                  {entry.changes.map((change) => (
                    <Box as="li" key={`${entry.id}-${change.field}`}>
                      <Text fontSize="sm" fontWeight="medium">
                        {change.field}
                      </Text>
                      <Text fontSize="sm" color="fg.muted">
                        {formatValue(change.old)} → {formatValue(change.new)}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>
        )}

        <Box>
          <Button
            onClick={() =>
              navigate({
                to: "/questions/$questionId/edit",
                params: { questionId },
              })
            }
          >
            Return to Question
          </Button>
        </Box>
      </VStack>
    </Container>
  )
}

export default QuestionAuditTimeline
